// pages/LostFound.jsx
import React, { useState, useRef, useEffect } from "react";
import { getFullImageUrl } from "../../middleware/Imagegetter";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Backdrop,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  Fade,
  Zoom,
  Slide,
  Grow,
  Avatar,
  Badge,
  Stack,
  Divider,
} from "@mui/material";
import {
  Search,
  AddCircle,
  Close,
  Sort,
  Share,
  Favorite,
  FavoriteBorder,
  LocationOn,
  Email,
  Image,
  Refresh,
  CloudUpload,
  CameraAlt,
  AutoAwesome,
  ArrowBack,
  CardGiftcard,
  CheckCircle,
  Error,
  Info,
  Warning,
  Dashboard,
  Groups,
  Event,
  Person,
  School,
  Settings,
  Help,
  Lightbulb,
  Notifications,
  Menu as MenuIcon,
  ChevronRight,
  ArrowForward,
  TrendingUp,
  MoreVert,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton";

import { Mail, School as SchoolIcon, BadgeCheck, Building2, Calendar } from "lucide-react";

// Import icons for sidebar
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import api from "../../api";

// New Theme based on PeerCampus Admin Dashboard
const peerCampusTheme = {
  primary: "#0d2a44",       // Deep blue
  secondary: "#016f57",     // Teal green
  accent: "#F59E0B",        // Amber
  background: "#F8FAFC",    // Light slate background
  surface: "#FFFFFF",       // White cards/surfaces
  text: "#1E293B",          // Slate 800
  textSecondary: "#64748B", // Slate 500
  border: "#E2E8F0",        // Slate 200
  success: "#016f57",
  warning: "#F59E0B",
  error: "#EF4444",
  info: "#3B82F6",
  sidebar: "#1E293B",       // Dark sidebar
};

const peerCampusGradient =
  "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)";

const gradientTextSx = {
  background: peerCampusGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: 'transparent',
  display: 'inline-block',
};

const gradientOutlinedButtonSx = {
  border: '1px solid transparent',
  background: `linear-gradient(${peerCampusTheme.surface}, ${peerCampusTheme.surface}) padding-box, ${peerCampusGradient} border-box`,
  '&:hover': {
    background: `linear-gradient(${peerCampusTheme.surface}, ${peerCampusTheme.surface}) padding-box, ${peerCampusGradient} border-box`,
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    color: peerCampusTheme.secondary,
  },
};

// Image styling constants for consistency
const imageStyles = {
  container: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: '12px',
    p: 1
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    maxHeight: '400px'
  },
  cardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    backgroundColor: '#F1F5F9',
    p: 1
  }
};

// Enhanced notification system
const EnhancedAlert = ({ severity, message, onClose, autoHide = true }) => (
  <motion.div
    initial={{ opacity: 0, x: 300, scale: 0.8 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    exit={{ opacity: 0, x: 300, scale: 0.8 }}
    transition={{ type: "spring", stiffness: 500, damping: 30 }}
  >
    <Alert 
      severity={severity} 
      variant="filled" 
      sx={{ 
        fontWeight: "bold",
        width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
        maxWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
        wordBreak: 'break-word',
        borderRadius: 3,
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        backdropFilter: "blur(10px)",
        background: severity === "error" ? 
          `linear-gradient(135deg, #EF4444, #DC2626)` :
          severity === "warning" ?
          `linear-gradient(135deg, #F59E0B, #EAB308)` :
          severity === "info" ?
          peerCampusGradient :
          peerCampusGradient
      }}
      onClose={onClose}
      iconMapping={{
        success: <CheckCircle sx={{ fontSize: 24 }} />,
        error: <Error sx={{ fontSize: 24 }} />,
        warning: <Warning sx={{ fontSize: 24 }} />,
        info: <Info sx={{ fontSize: 24 }} />,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {message}
      </motion.div>
    </Alert>
  </motion.div>
);

// Reusable inline loader component
const InlineLoader = ({ size = 24, color = peerCampusTheme.primary }) => {
  const loaderSize = typeof size === 'number' ? size : 24;
  
  return (
    <Box sx={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      position: 'relative',
      width: loaderSize,
      height: loaderSize
    }}>
      {/* Static background circle */}
      <Box sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        border: `2px solid ${color}20`
      }} />
      
      {/* Animated arc */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ 
          duration: 1, 
          repeat: Infinity, 
          ease: "linear" 
        }}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: `2px solid transparent`,
          borderTop: `2px solid ${color}`,
          borderRight: `2px solid ${color}80`
        }}
      />
    </Box>
  );
};

// Modern Smooth Loading Overlay
const LoadingOverlay = ({ message = "Loading...", subMessage = "Please wait" }) => (
  <Backdrop
    sx={{ 
      color: '#fff', 
      zIndex: (theme) => theme.zIndex.drawer + 1,
      background: `linear-gradient(135deg, ${peerCampusTheme.background}EE, ${peerCampusTheme.surface}EE)`,
      backdropFilter: "blur(10px)",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}
    open={true}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ 
        textAlign: 'center',
        maxWidth: '400px',
        width: '90%'
      }}
    >
      {/* Modern Loader Animation */}
      <Box sx={{ position: 'relative', width: 100, height: 100, mx: 'auto', mb: 4 }}>
        {/* Outer ring */}
        <motion.div
          animate={{ 
            rotate: 360,
            borderWidth: [3, 4, 3],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{ 
            rotate: { duration: 1.5, repeat: Infinity, ease: "linear" },
            borderWidth: { duration: 1.5, repeat: Infinity },
            opacity: { duration: 1.5, repeat: Infinity }
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: `3px solid ${peerCampusTheme.primary}30`,
            borderTop: `3px solid ${peerCampusTheme.primary}`,
            borderRight: `3px solid ${peerCampusTheme.primary}`,
          }}
        />
        
        {/* Inner ring */}
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 2, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity }
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '20%',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: `2px solid ${peerCampusTheme.secondary}30`,
            borderBottom: `2px solid ${peerCampusTheme.secondary}`,
            borderLeft: `2px solid ${peerCampusTheme.secondary}`,
          }}
        />
        
        {/* Center dot */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            backgroundColor: [peerCampusTheme.primary, peerCampusTheme.secondary, peerCampusTheme.primary]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity
          }}
          style={{
            position: 'absolute',
            top: '40%',
            left: '40%',
            width: '20%',
            height: '20%',
            borderRadius: '50%',
            backgroundColor: peerCampusTheme.primary,
          }}
        />
      </Box>
      
      {/* Text Content */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Typography variant="h5" gutterBottom sx={{ 
          fontWeight: 'bold',
          background: peerCampusGradient,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          mb: 1
        }}>
          {message}
        </Typography>
        
        <Typography variant="body1" color={peerCampusTheme.textSecondary} sx={{ mb: 3 }}>
          {subMessage}
        </Typography>
      </motion.div>

      {/* Progress dots */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            animate={{ 
              y: [0, -8, 0],
              scale: [1, 1.2, 1]
            }}
            transition={{ 
              duration: 1.2,
              repeat: Infinity,
              delay: index * 0.2,
              ease: "easeInOut"
            }}
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: peerCampusGradient,
            }}
          />
        ))}
      </Box>
    </motion.div>
  </Backdrop>
);

// Stats Card Component (matching the dashboard style)
const StatsCard = ({ title, value, icon, color, trend }) => (
  <Card sx={{ 
    p: 3, 
    borderRadius: 2,
    border: `1px solid ${peerCampusTheme.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Box sx={{ 
        p: 1.5, 
        borderRadius: 2,
        background: `${color}10`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {React.cloneElement(icon, { sx: { color: color, fontSize: 24 } })}
      </Box>
      {trend && (
        <Chip 
          label={trend} 
          size="small" 
          color={trend.includes('+') ? "success" : "error"}
          sx={{ fontSize: '0.7rem', height: 24 }}
        />
      )}
    </Box>
    <Typography variant="h4" fontWeight="bold" color={peerCampusTheme.text} gutterBottom>
      {value}
    </Typography>
    <Typography variant="body2" color={peerCampusTheme.textSecondary}>
      {title}
    </Typography>
  </Card>
);

// Section Header Component
const SectionHeader = ({ title, subtitle, action, onActionClick }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Box>
        <Typography variant="h5" fontWeight="bold" color={peerCampusTheme.text}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color={peerCampusTheme.textSecondary}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && (
        <Button
          variant="outlined"
          size="small"
          endIcon={<ChevronRight />}
          onClick={onActionClick}
          sx={{ 
            borderColor: peerCampusTheme.border,
            color: peerCampusTheme.text,
            '&:hover': {
              borderColor: peerCampusTheme.primary,
              backgroundColor: `${peerCampusTheme.primary}05`
            }
          }}
        >
          {action}
        </Button>
      )}
    </Box>
    <Divider sx={{ borderColor: peerCampusTheme.border }} />
  </Box>
);

export default function LostFound({ embedded = false } = {}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const [mode, setMode] = useState(null);
  const [itemsLost, setItemsLost] = useState([]);
  const [itemsFound, setItemsFound] = useState([]);
  const [newItem, setNewItem] = useState({
    title: "",
    description: "",
    location: "",
    image: null,
    imageFile: null,
    email: "",
    reward: "",
  });
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid");
  const [detailDialog, setDetailDialog] = useState({ open: false, item: null });
  const [shareMenu, setShareMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successAction, setSuccessAction] = useState({ text: null, action: null });
  
  // Email confirmation state
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [emailConfirmationData, setEmailConfirmationData] = useState({
    ownerEmail: '',
    itemName: ''
  });

  // Contact owner (email) loading state (separate from generic page loading)
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // Email success popup state
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);
  const [emailSuccessData, setEmailSuccessData] = useState({
    ownerEmail: '',
    itemName: ''
  });
  
  // Reward states
  const [offerReward, setOfferReward] = useState(false);
  const [reward, setReward] = useState("");
  
  // Image scanning states
  const [scanDialog, setScanDialog] = useState(false);
  const [scanningImage, setScanningImage] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [showEnhanceOption, setShowEnhanceOption] = useState(false);
  const [idfound, setidfound] = useState(null);

  // Camera states
  const [cameraDialog, setCameraDialog] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraImage, setCameraImage] = useState("");
  const videoRef = useRef(null);
  
  const routerLocation = useLocation();
  const footerRef = useRef(null);
  const fileInputRef = useRef(null);
  const scanFileInputRef = useRef(null);
  const browseSectionRef = useRef(null);

  const normalizeListResponse = (data) => {
    // Our `api` module returns response.data directly.
    // DRF may return either an array or a paginated shape: { results: [...] }.
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
  };

  const scrollToBrowseSection = () => {
    const el = browseSectionRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getLostFoundBasePath = () => {
    const path = String(routerLocation?.pathname || '').toLowerCase();
    if (path.startsWith('/student/lostfound')) return '/student/lostfound';
    if (path.startsWith('/clubmember/lostfound')) return '/clubmember/lostfound';
    return '/lostfound';
  };

  const redirectToBrowse = (targetMode) => {
    const params = new URLSearchParams(routerLocation.search);
    params.set('mode', targetMode);
    params.set('section', 'browse');
    navigate({ pathname: getLostFoundBasePath(), search: `?${params.toString()}` });

    // Ensure UI switches even if we're already on /lostfound
    setMode(targetMode);
    setViewMode('grid');
    setTimeout(scrollToBrowseSection, 250);
  };

  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const modeParam = params.get('mode');
    const sectionParam = params.get('section');

    if (modeParam === 'lost' || modeParam === 'found') {
      setMode(modeParam);
      setViewMode('grid');
    }

    if (sectionParam === 'browse') {
      setTimeout(scrollToBrowseSection, 250);
    }
  }, [routerLocation.search]);

  // Enhanced notification handler
  const showNotification = (message, type = "success", duration = 4000) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, duration);
  };

  // Enhanced success handler
  const showSuccessMessage = (message, actionText = null, action = null) => {
    setSuccessMessage(message);
    setSuccessAction({ text: actionText, action });
    setShowSuccess(true);
  };

  // Define sidebar items
  const sidebarItems = [
    { 
      label: 'Dashboard', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <Dashboard />
    },
    { 
      label: 'Lost & Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <Event />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <Lightbulb />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <Help />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <Settings />
    },
  ];

  // Define quick actions 
  const quickActions = [
    {
      title: "Report Lost Item",
      description: "Report a lost item on campus",
      icon: <Search color="primary" />,
      color: peerCampusTheme.primary,
      action: () => setMode("lost")
    },
    {
      title: "Add Found Item",
      description: "Add an item you found",
      icon: <AddCircle color="success" />,
      color: peerCampusTheme.success,
      action: () => setScanDialog(true)
    },
    {
      title: "Browse Lost Items",
      description: "View all reported lost items",
      icon: <LostFoundIcon color="warning" />,
      color: peerCampusTheme.warning,
      action: () => redirectToBrowse('lost')
    },
    {
      title: "Browse Found Items",
      description: "View all found items",
      icon: <CheckCircle color="info" />,
      color: peerCampusTheme.info,
      action: () => redirectToBrowse('found')
    }
  ];

  // Enhanced matching functions
  const extractKeywords = (text) => {
    if (!text) return [];
    const words = text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 3);
    return [...new Set(words)];
  };

  const calculateMatchScore = (searchTerms, targetText) => {
    if (!searchTerms.length || !targetText) return 0;
    let score = 0;
    searchTerms.forEach(term => {
      if (targetText.includes(term)) {
        score += 1;
      }
    });
    return score / searchTerms.length;
  };

  const checkDescriptionSimilarity = (desc1, desc2) => {
    if (!desc1 || !desc2) return 0;
    
    const words1 = new Set(desc1.toLowerCase().split(/\s+/));
    const words2 = new Set(desc2.toLowerCase().split(/\s+/));
    
    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);
    
    return union.size > 0 ? intersection.size / union.size : 0;
  };

  const performEnhancedMatching = async (description, title) => {
    try {
      const searchTerms = extractKeywords(description + " " + title);
      
      const potentialMatches = itemsLost.filter(lostItem => {
        const lostText = (lostItem.title + " " + lostItem.description + " " + lostItem.location).toLowerCase();
        const foundText = (title + " " + description).toLowerCase();
        
        const matchScore = calculateMatchScore(searchTerms, lostText);
        const descriptionMatch = checkDescriptionSimilarity(description, lostItem.description);
        const titleMatch = checkDescriptionSimilarity(title, lostItem.title);
        
        const totalScore = (matchScore * 0.4) + (descriptionMatch * 0.4) + (titleMatch * 0.2);
        
        return totalScore > 0.3;
      });
      
      return potentialMatches;
    } catch (error) {
      console.error("Enhanced matching error:", error);
      return [];
    }
  };

  // Handle contact action for matched items
  const handleContactOwner = async (item) => {
    try {
      const ownerEmail = item?.owner_email || item?.owner?.email || item?.email;
      if (!ownerEmail) {
        showNotification("Owner email not available for this item.", "error");
        return;
      }

      // Close dialogs immediately so the UI doesn't look stuck
      setScanDialog(false);
      setDetailDialog({ open: false, item: null });
      setScanningImage("");
      setScanResult(null);
      setShowEnhanceOption(false);

      setIsSendingEmail(true);
      
      const response = await api.post('lostfound/send-found-mail/', {
        "lost_item_user": ownerEmail,
        "lost_item_name": item.title,
        "lost_item_image": item.image
      });
      
      if (response?.message) {
        // Set success data for the popup
        setEmailSuccessData({
          ownerEmail,
          itemName: item.title
        });

        // Show the success popup
        setShowEmailSuccess(true);
        
        // Also show a subtle notification
        showNotification(`Email sent to ${ownerEmail}`, "success", 3000);
      } else {
        showNotification("Some error occurred while sending email", "error");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showNotification("Failed to send email. Please try again.", "error");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Sort items by date (most recent first)
  const sortItemsByDate = (items) => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a.created_at || a.date || 0);
      const dateB = new Date(b.created_at || b.date || 0);
      return dateB - dateA;
    });
  };

  // Load saved items with enhanced loading
  useEffect(() => {
    setLoading(true);
    
    const fetchItems = async () => {
      try {
        const [lostResponse, foundResponse] = await Promise.all([
          api.get('lostfound/lost-items/'),
          api.get('lostfound/found-items/')
        ]);

        const lostData = normalizeListResponse(lostResponse);
        const formattedLostData = lostData.map(item => ({
          id: item.id || `lost-${Math.random()}`,
          title: item.title || "",
          description: item.description || "",
          location: item.location || "",
          image: getFullImageUrl(item.image),
          email: item.owner?.email || "",
          status: item.status || "active",
          created_at: item.created_at || new Date().toISOString(),
          owner: item.owner || {},
          likes: item.likes || 0,
          reward: item.reward || ""
        }));

        const foundData = normalizeListResponse(foundResponse);
        const formattedFoundData = foundData.map(item => ({
          id: item.id || `found-${Math.random()}`,
          title: item.title || "",
          description: item.description || "",
          location: item.location || "",
          image: getFullImageUrl(item.image),
          email: item.owner?.email || "",
          status: item.status || "active",
          created_at: item.created_at || new Date().toISOString(),
          owner: item.owner || {},
          likes: item.likes || 0,
          reward: item.reward || ""
        }));

        setItemsLost(sortItemsByDate(formattedLostData));
        setItemsFound(sortItemsByDate(formattedFoundData));

        showNotification("Items loaded successfully!", "success");

      } catch (error) {
        // console.log("Error fetching items:", error);
        setItemsLost([]);
        setItemsFound([]);
        showNotification("Failed to load items. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    };

    setTimeout(() => {
      fetchItems();
    }, 1000);
  }, []);

  // Get recent items (5 most recent)
  const getRecentItems = (items) => {
    return sortItemsByDate([...items]).slice(0, 5);
  };

  // Camera functions
  const handleOpenCamera = () => {
    setCameraDialog(true);
    startCamera();
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      showNotification("Cannot access camera. Please check permissions.", "error");
    }
  };

  const handleCaptureImage = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const imageData = canvas.toDataURL('image/jpeg');
      setCameraImage(imageData);
      
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handleRetakePhoto = () => {
    setCameraImage("");
    startCamera();
  };

  const handleCloseCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    setCameraDialog(false);
    setCameraImage("");
  };

  // Direct image search API call
  const handleDirectImageSearch = async (imageFile, enhanced = false) => {
    setLoading(true);
    setScanResult(null);
    setShowEnhanceOption(false);
    
    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("title", "Scanned Found Item");
      formData.append("description", "Item found via image scan");
      formData.append("location", "Unknown Location");
      formData.append("enhanced", enhanced.toString());

      const response = await api.post('lostfound/direct-image-search/', formData);
      
      const responseData = response;
      setidfound(responseData.search_id);
      
      if (responseData.matches && responseData.matches.length > 0) {
        const formattedMatches = responseData.matches.map(match => ({
          ...match,
          image: getFullImageUrl(match.image)
        }));
        setScanResult({
          success: true,
          message: `🎉 Item Found! We found ${responseData.matches.length} potential match(es) in our lost items database.`,
          matches: formattedMatches,
          searchId: responseData.search_id
        });
      } else {
        setScanResult({
          success: false,
          message: "No matches found in our current lost items database."
        });
        setShowEnhanceOption(true);
      }
      
    } catch (error) {
      console.error("Direct image search error:", error);
      setScanResult({
        success: false,
        message: `Search failed: ${error.response?.data?.error || error.message || "Unknown error"}`
      });
      setShowEnhanceOption(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle scan image upload
  const handleScanImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setScanningImage(event.target.result);
        handleDirectImageSearch(file, false);
      };
      reader.readAsDataURL(file);
    }
  };

  // Use captured image for direct search
  const handleUseCapturedImage = () => {
    if (cameraImage) {
      fetch(cameraImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], "captured-image.jpg", { type: "image/jpeg" });
          setScanningImage(cameraImage);
          setCameraDialog(false);
          handleDirectImageSearch(file, false);
        });
    }
  };

  // Reset scan dialog
  const resetScanDialog = () => {
    setScanDialog(false);
    setScanningImage("");
    setScanResult(null);
    setShowEnhanceOption(false);
    setOfferReward(false);
    setReward("");
  };

  // Open enhance version (full details form)
  const handleEnhanceVersion = () => {
    setScanDialog(false);
    setMode("found");
    
    if (scanningImage) {
      if (scanningImage.startsWith('data:')) {
        fetch(scanningImage)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], "enhanced-item.jpg", { type: "image/jpeg" });
            setNewItem({ 
              ...newItem, 
              image: scanningImage,
              imageFile: file
            });
          });
      } else {
        setNewItem({ ...newItem, image: scanningImage });
      }
    }
    
    if (scanResult && !scanResult.success) {
      setNewItem(prev => ({
        ...prev,
        title: prev.title || "Found Item (Enhanced Search)",
        description: prev.description || "Item found via enhanced image matching"
      }));
    }
  };

  // Handle file upload with preview AND store file object
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewItem({ 
          ...newItem, 
          image: event.target.result,
          imageFile: file
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Enhanced handle add item
  const handleAddItem = async (type) => {
    if (!newItem.title || !newItem.description || !newItem.email) {
      showNotification("Please fill in all required fields", "error");
      return;
    }

    const formData = new FormData();
    formData.append("title", newItem.title);
    formData.append("description", newItem.description);
    formData.append("location", newItem.location);
    
    if (type === "found" && newItem.reward) {
      formData.append("reward", newItem.reward);
    }
    
    if (newItem.imageFile) {
      formData.append("image", newItem.imageFile);
    }

    setLoading(true);

    try {
      let response;
      let newItemId;
      
      if (type === "lost") {
        response = await api.post('lostfound/lost-items/', formData);
        
        if (response) {
          const newItemWithImage = {
            ...response,
            image: getFullImageUrl(response.image),
            email: newItem.email,
            likes: 0,
            matched: false,
            reward: newItem.reward || ""
          };
          
          const updatedLost = sortItemsByDate([...itemsLost, newItemWithImage]);
          setItemsLost(updatedLost);
          newItemId = response.id;
        }
        
      } else {
        if (idfound) {
          response = await api.patch(`lostfound/found-items/${idfound}/`, formData);
          newItemId = idfound;
        } else {
          response = await api.post('lostfound/found-items/', formData);
          newItemId = response?.id;
        }
        
        if (response) {
          const newItemWithImage = {
            ...response,
            image: getFullImageUrl(response.image),
            email: newItem.email,
            likes: 0,
            claimed: false,
            reward: newItem.reward || ""
          };
          
          const updatedFound = sortItemsByDate([...itemsFound, newItemWithImage]);
          setItemsFound(updatedFound);
        }
      }

      setNewItem({ 
        title: "", 
        description: "", 
        location: "", 
        image: null, 
        imageFile: null,
        email: "", 
        reward: ""
      });
      setOfferReward(false);
      setReward("");

      if (type === "found" && newItemId) {
        setLoading(true);
        try {
          const enhancedMatches = await performEnhancedMatching(
            newItem.description, 
            newItem.title
          );
          
          if (enhancedMatches.length > 0) {
            setScanResult({
              success: true,
              message: `🎉 Enhanced Search Found ${enhancedMatches.length} potential match(es)!`,
              matches: enhancedMatches,
              searchId: newItemId,
              enhanced: true
            });
            setScanDialog(true);
          } else {
            if (newItem.imageFile) {
              try {
                const matchResponse = await api.get(`lostfound/found-items/${newItemId}/match-lost-items/`);
                
                if (Array.isArray(matchResponse) && matchResponse.length > 0) {
                  setScanResult({
                    success: true,
                    message: `🎉 Item Found! We found ${matchResponse.length} potential match(es) in our lost items database.`,
                    matches: matchResponse,
                    searchId: newItemId
                  });
                  setScanDialog(true);
                } else {
                  showSuccessMessage(
                    `Your found item has been submitted successfully! No matches found yet, but we'll notify you if any appear.`,
                    "View All Items",
                    () => redirectToBrowse(type)
                  );
                }
              } catch (matchError) {
                console.error("Image match error:", matchError);
                showSuccessMessage(
                  `Your found item has been submitted successfully!`,
                  "View All Items",
                  () => redirectToBrowse(type)
                );
              }
            } else {
              showSuccessMessage(
                `Your found item has been submitted successfully!`,
                "View All Items",
                () => redirectToBrowse(type)
              );
            }
          }
        } catch (matchError) {
          console.error("Enhanced match error:", matchError);
          showSuccessMessage(
            `Your found item has been submitted successfully!`,
            "View All Items",
            () => redirectToBrowse(type)
          );
        } finally {
          setLoading(false);
        }
      } else if (type === "lost") {
        showSuccessMessage(
          `Your lost item report has been submitted successfully!`,
          "View All Items",
          () => redirectToBrowse(type)
        );
        setLoading(false);
      }

      const searchParams = new URLSearchParams(routerLocation.search);
      const redirectTo = searchParams.get('redirect');
      const itemType = searchParams.get('type') || type;

      if (redirectTo === 'yourlostfound') {
        setTimeout(() => {
          navigate(`/yourlostfound?newItemAdded=true&itemType=${itemType}`);
        }, 2000);
      }
        
    } catch (error) {
      console.error("Error adding item:", error);
      showNotification(
        `Failed to add item: ${error.response?.data?.error || error.message || "Unknown error"}`,
        "error"
      );
      setLoading(false);
    }
  };

  // Filter and sort items
  const filteredItems = (mode === "lost" ? itemsLost : itemsFound)
    .filter(item =>
      (item.title?.toLowerCase().includes(search.toLowerCase()) ||
       item.description?.toLowerCase().includes(search.toLowerCase()) ||
       item.location?.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.created_at || b.date) - new Date(a.created_at || a.date);
        case "oldest": return new Date(a.created_at || a.date) - new Date(b.created_at || b.date);
        case "likes": return (b.likes || 0) - (a.likes || 0);
        default: return 0;
      }
    });

  // Like item function
  const handleLike = (id, type) => {
    const setItems = type === "lost" ? setItemsLost : setItemsFound;
    const items = type === "lost" ? itemsLost : itemsFound;
    
    const updatedItems = items.map(item =>
      item.id === id ? { ...item, likes: (item.likes || 0) + 1 } : item
    );
    
    setItems(updatedItems);
    showNotification("Item liked!", "success", 2000);
  };

  // Share item function
  const handleShare = (item, event) => {
    setShareMenu(event.currentTarget);
    if (navigator.share) {
      navigator.share({
        title: `${mode === "lost" ? 'Lost' : 'Found'}: ${item.title}`,
        text: item.description,
        url: window.location.href,
      });
    }
    showNotification("Share options opened!", "info", 2000);
  };

  // Item card component (updated to match new theme)
  const ItemCard = ({ item, type }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -20 }}
      whileHover={{ 
        y: isMobile ? 0 : -4, 
        transition: { duration: 0.2, ease: "easeOut" }
      }}
    >
      <Card 
        sx={{ 
          background: peerCampusTheme.surface,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${peerCampusTheme.border}`,
          cursor: 'pointer',
          height: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }
        }}
        onClick={() => setDetailDialog({ open: true, item: { ...item, type } })}
      >
        <CardMedia
          component="img"
          height={isMobile ? 140 : 200}
          image={item.image || "/fallback-image.jpg"}
          alt={item.title}
          sx={{ 
            ...imageStyles.cardImg,
            height: isMobile ? 140 : 200,
          }}
          onError={(e) => {
            e.target.src = "/fallback-image.jpg";
            e.target.style.objectFit = 'contain';
          }}
        />
        
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography 
              variant="subtitle1" 
              fontWeight="600" 
              color={peerCampusTheme.text} 
              gutterBottom
              sx={{ 
                fontSize: '0.95rem',
                lineHeight: 1.2
              }}
            >
              {item.title}
            </Typography>
            <Chip 
              label={type === "lost" ? "Lost" : "Found"} 
              size="small" 
              color={type === "lost" ? "error" : "success"}
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          </Box>
          
          <Typography 
            variant="body2" 
            color={peerCampusTheme.textSecondary} 
            sx={{ 
              mb: 2, 
              height: 40, 
              overflow: 'hidden',
              fontSize: '0.85rem',
              lineHeight: 1.4
            }}
          >
            {item.description}
          </Typography>
          
          {/* Reward Display */}
          {item.reward && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip 
                label={`Reward: ${item.reward}`} 
                size="small" 
                sx={{ 
                  fontSize: '0.65rem',
                  height: 20,
                  backgroundColor: `${peerCampusTheme.warning}15`,
                  color: peerCampusTheme.warning
                }}
              />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationOn sx={{ fontSize: 14, mr: 0.5, color: peerCampusTheme.textSecondary }} />
            <Typography 
              variant="caption" 
              color={peerCampusTheme.textSecondary}
              sx={{ fontSize: '0.75rem' }}
            >
              {item.location}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Email sx={{ fontSize: 14, mr: 0.5, color: peerCampusTheme.textSecondary }} />
            <Typography 
              variant="caption" 
              color={peerCampusTheme.textSecondary}
              sx={{ 
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {item.email}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Like">
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleLike(item.id, type); }}
                  sx={{ color: peerCampusTheme.textSecondary, p: 0.5 }}
                >
                  <FavoriteBorder sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Typography 
                variant="caption" 
                color={peerCampusTheme.textSecondary}
                sx={{ fontSize: '0.75rem' }}
              >
                {item.likes || 0}
              </Typography>
            </Box>
            
            <Typography 
              variant="caption" 
              color={peerCampusTheme.textSecondary}
              sx={{ fontSize: '0.75rem' }}
            >
              {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Unknown date'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  // Get recent items for display
  const recentLostItems = getRecentItems(itemsLost);
  const recentFoundItems = getRecentItems(itemsFound);
  
  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: embedded ? "auto" : "100vh", 
      bgcolor: peerCampusTheme.background,
      color: peerCampusTheme.text,
    }}>
      {/* Navbar */}
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}

      {/* Sending Email Overlay (Contact Owner) */}
      {isSendingEmail && (
        <LoadingOverlay
          message="Sending email..."
          subMessage="Notifying the item owner"
        />
      )}

      {/* Loading Overlay */}
      {loading && <LoadingOverlay 
        message="Processing your request..." 
        subMessage="This will just take a moment"
      />}

      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Sidebar */}
        {!embedded && !isMobile && <GlobalSidebar />}
        
        {/* Main Content */}
        <Box component="main" sx={{ 
          flexGrow: 1, 
          p: embedded ? 0 : { xs: 2, sm: 3, md: 4 },
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {!embedded && !mode && (
            <BackButton sx={{ mb: 2 , backgroundColor: "#dfdfdff", color: "#000" }} />
          )}
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
            <AnimatePresence mode="wait">
              {!mode ? (
                <motion.div
                  key="main"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Welcome Header */}
                  <Box sx={{ mb: { xs: 3, md: 4 } }}>
                    <Typography 
                      variant={isMobile ? "h5" : "h4"} 
                      fontWeight="bold" 
                      color={peerCampusTheme.text}
                      sx={{ mb: 1 }}
                    >
                      Lost & Found Portal
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color={peerCampusTheme.textSecondary}
                    >
                      Welcome back! Reunite people with their lost items through our smart matching system.
                    </Typography>
                  </Box>

                  {/* Stats Section */}
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={6} sm={3}>
                      <StatsCard 
                        title="Items Lost" 
                        value={itemsLost.length} 
                        icon={<Search />} 
                        color={peerCampusTheme.error}
                        trend="+12%"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatsCard 
                        title="Items Found" 
                        value={itemsFound.length} 
                        icon={<CheckCircle />} 
                        color={peerCampusTheme.success}
                        trend="+8%"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatsCard 
                        title="Successful Matches" 
                        value={
                          itemsLost.filter(item => (item.status || 'active') === 'deactive').length +
                          itemsFound.filter(item => (item.status || 'active') === 'deactive').length
                        }
                        icon={<AutoAwesome />} 
                        color={peerCampusTheme.primary}
                        trend="+24%"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatsCard 
                        title="Active Searches" 
                        value={
                          itemsLost.filter(item => (item.status || 'active') === 'active').length +
                          itemsFound.filter(item => (item.status || 'active') === 'active').length
                        }
                        icon={<TrendingUp />} 
                        color={peerCampusTheme.warning}
                        trend="+16%"
                      />
                    </Grid>
                  </Grid>

                  {/* Quick Actions Section */}
                  <SectionHeader 
                    title="Quick Actions" 
                    subtitle="Streamline your lost & found tasks"
                  />
                  
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {quickActions.map((action, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card 
                            sx={{ 
                              p: 2.5, 
                              borderRadius: 2,
                              border: `1px solid ${peerCampusTheme.border}`,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                borderColor: action.color,
                                boxShadow: `0 4px 20px ${action.color}20`,
                              }
                            }}
                            onClick={action.action}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1, 
                                borderRadius: 2,
                                background: `${action.color}10`,
                                mr: 2
                              }}>
                                {React.cloneElement(action.icon, { sx: { color: action.color } })}
                              </Box>
                              <Typography variant="subtitle1" fontWeight="600">
                                {action.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                              {action.description}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                              <ChevronRight sx={{ color: peerCampusTheme.textSecondary, fontSize: 20 }} />
                            </Box>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Recent Items Section */}
                  <Grid container spacing={3}>
                    {/* Lost Items */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ 
                        p: 3, 
                        borderRadius: 2,
                        border: `1px solid ${peerCampusTheme.border}`,
                        height: '100%'
                      }}>
                        <SectionHeader 
                          title="Recently Lost Items" 
                          subtitle="Active lost item reports"
                          action="View All"
                          onActionClick={() => redirectToBrowse('lost')}
                        />
                        
                        {loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <InlineLoader size={40} />
                          </Box>
                        ) : recentLostItems.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Search sx={{ fontSize: 48, color: peerCampusTheme.textSecondary, mb: 2 }} />
                            <Typography variant="body1" color={peerCampusTheme.textSecondary}>
                              No lost items found
                            </Typography>
                          </Box>
                        ) : (
                          <Stack spacing={2}>
                            {recentLostItems.map((item, index) => (
                              <motion.div
                                key={item.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                              >
                                <Box 
                                  sx={{ 
                                    p: 2, 
                                    borderRadius: 2,
                                    border: `1px solid ${peerCampusTheme.border}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: `${peerCampusTheme.primary}05`,
                                      borderColor: peerCampusTheme.primary,
                                    }
                                  }}
                                  onClick={() => setDetailDialog({ open: true, item: { ...item, type: "lost" } })}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ 
                                      width: 40, 
                                      height: 40, 
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      mr: 2,
                                      flexShrink: 0
                                    }}>
                                      <img 
                                        src={item.image || "/fallback-image.jpg"} 
                                        alt={item.title}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.src = "/fallback-image.jpg";
                                          e.target.style.objectFit = 'contain';
                                        }}
                                      />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="subtitle2" fontWeight="600" noWrap>
                                        {item.title}
                                      </Typography>
                                      <Typography variant="caption" color={peerCampusTheme.textSecondary} noWrap>
                                        {item.location}
                                      </Typography>
                                    </Box>
                                    <Chip 
                                      label="Lost" 
                                      size="small" 
                                      color="error"
                                      sx={{ fontSize: '0.6rem', height: 20 }}
                                    />
                                  </Box>
                                </Box>
                              </motion.div>
                            ))}
                          </Stack>
                        )}
                      </Card>
                    </Grid>

                    {/* Found Items */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ 
                        p: 3, 
                        borderRadius: 2,
                        border: `1px solid ${peerCampusTheme.border}`,
                        height: '100%'
                      }}>
                        <SectionHeader 
                          title="Recently Found Items" 
                          subtitle="Items waiting to be claimed"
                          action="View All"
                          onActionClick={() => redirectToBrowse('found')}
                        />
                        
                        {loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <InlineLoader size={40} />
                          </Box>
                        ) : recentFoundItems.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CheckCircle sx={{ fontSize: 48, color: peerCampusTheme.textSecondary, mb: 2 }} />
                            <Typography variant="body1" color={peerCampusTheme.textSecondary}>
                              No found items yet
                            </Typography>
                          </Box>
                        ) : (
                          <Stack spacing={2}>
                            {recentFoundItems.map((item, index) => (
                              <motion.div
                                key={item.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 + 0.2 }}
                              >
                                <Box 
                                  sx={{ 
                                    p: 2, 
                                    borderRadius: 2,
                                    border: `1px solid ${peerCampusTheme.border}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: `${peerCampusTheme.success}05`,
                                      borderColor: peerCampusTheme.success,
                                    }
                                  }}
                                  onClick={() => setDetailDialog({ open: true, item: { ...item, type: "found" } })}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ 
                                      width: 40, 
                                      height: 40, 
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      mr: 2,
                                      flexShrink: 0
                                    }}>
                                      <img 
                                        src={item.image || "/fallback-image.jpg"} 
                                        alt={item.title}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.src = "/fallback-image.jpg";
                                          e.target.style.objectFit = 'contain';
                                        }}
                                      />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="subtitle2" fontWeight="600" noWrap>
                                        {item.title}
                                      </Typography>
                                      <Typography variant="caption" color={peerCampusTheme.textSecondary} noWrap>
                                        {item.location}
                                      </Typography>
                                    </Box>
                                    <Chip 
                                      label="Found" 
                                      size="small" 
                                      color="success"
                                      sx={{ fontSize: '0.6rem', height: 20 }}
                                    />
                                  </Box>
                                </Box>
                              </motion.div>
                            ))}
                          </Stack>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                >
                  {/* Form Header */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        <IconButton 
                          onClick={() => setMode(null)}
                          sx={{ 
                            mr: 2,
                            background: peerCampusTheme.surface,
                            border: `1px solid ${peerCampusTheme.border}`,
                            '&:hover': { background: peerCampusTheme.background }
                          }}
                        >
                          <ArrowBack />
                        </IconButton>
                      </motion.div>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color={peerCampusTheme.text}>
                          {mode === "lost" ? "Report Lost Item" : "Add Found Item"}
                        </Typography>
                        <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                          {mode === "lost" 
                            ? "Provide details about your lost item to help us find it" 
                            : "Add details about an item you found to help reunite it with its owner"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant={mode === "lost" ? "contained" : "outlined"}
                        onClick={() => setMode("lost")}
                        sx={{ 
                          borderRadius: 2,
                          textTransform: 'none',
                          px: 3,
                          ...(mode === "lost"
                            ? {
                                background: peerCampusGradient,
                                color: 'white',
                                '&:hover': { background: peerCampusGradient },
                              }
                            : gradientOutlinedButtonSx)
                        }}
                      >
                        {mode === 'lost' ? 'Report Lost' : <Box component="span" sx={gradientTextSx}>Report Lost</Box>}
                      </Button>
                      <Button
                        variant={mode === "found" ? "contained" : "outlined"}
                        onClick={() => setMode("found")}
                        sx={{ 
                          borderRadius: 2,
                          textTransform: 'none',
                          px: 3,
                          ...(mode === "found"
                            ? {
                                background: peerCampusGradient,
                                color: 'white',
                                '&:hover': { background: peerCampusGradient },
                              }
                            : gradientOutlinedButtonSx)
                        }}
                      >
                        {mode === 'found' ? 'Add Found' : <Box component="span" sx={gradientTextSx}>Add Found</Box>}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CameraAlt />}
                        onClick={() => setScanDialog(true)}
                        sx={{ 
                          borderRadius: 2,
                          textTransform: 'none',
                          px: 3,
                          ml: 'auto',
                          ...gradientOutlinedButtonSx,
                        }}
                      >
                        <Box component="span" sx={gradientTextSx}>Scan Item</Box>
                      </Button>
                    </Box>
                  </Box>

                  {/* Form Content */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                      <Card sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                          Item Details
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Item Title"
                              value={newItem.title}
                              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                              sx={{ mb: 2 }}
                              InputProps={{
                                sx: { borderRadius: 1 }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Description"
                              multiline
                              rows={3}
                              value={newItem.description}
                              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                              sx={{ mb: 2 }}
                              InputProps={{
                                sx: { borderRadius: 1 }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Location"
                              value={newItem.location}
                              onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                              sx={{ mb: 2 }}
                              InputProps={{
                                sx: { borderRadius: 1 }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Your Email"
                              type="email"
                              value={newItem.email}
                              onChange={(e) => setNewItem({ ...newItem, email: e.target.value })}
                              sx={{ mb: 2 }}
                              InputProps={{
                                sx: { borderRadius: 1 }
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={offerReward}
                                  onChange={(e) => {
                                    setOfferReward(e.target.checked);
                                    if (!e.target.checked) {
                                      setReward("");
                                      setNewItem({ ...newItem, reward: "" });
                                    }
                                  }}
                                />
                              }
                              label="Offer reward for return"
                            />
                            
                            {offerReward && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                transition={{ duration: 0.3 }}
                              >
                                <TextField
                                  fullWidth
                                  label="Reward Details"
                                  value={reward}
                                  onChange={(e) => {
                                    setReward(e.target.value);
                                    setNewItem({ ...newItem, reward: e.target.value });
                                  }}
                                  placeholder="e.g., $50, Gift card, etc."
                                  sx={{ mt: 1 }}
                                  InputProps={{
                                    sx: { borderRadius: 1 }
                                  }}
                                />
                              </motion.div>
                            )}
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                      <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                          Item Image
                        </Typography>
                        
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="upload-button"
                          type="file"
                          onChange={handleImageUpload}
                          ref={fileInputRef}
                        />
                        
                        {!newItem.image ? (
                          <label htmlFor="upload-button">
                            <Paper
                              sx={{
                                p: 4,
                                border: `2px dashed ${peerCampusTheme.border}`,
                                borderRadius: 2,
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: peerCampusTheme.primary,
                                  backgroundColor: `${peerCampusTheme.primary}05`
                                }
                              }}
                            >
                              <CloudUpload sx={{ fontSize: 48, color: peerCampusTheme.textSecondary, mb: 2 }} />
                              <Typography variant="body1" gutterBottom sx={{ color: peerCampusTheme.text }}>
                                Upload Item Image
                              </Typography>
                              <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                                Click to upload or drag and drop
                              </Typography>
                            </Paper>
                          </label>
                        ) : (
                          <Box>
                            <Box sx={{ 
                              width: '100%', 
                              display: 'flex', 
                              justifyContent: 'center',
                              mb: 2 
                            }}>
                              <img
                                src={newItem.image}
                                alt="preview"
                                style={{
                                  width: "100%",
                                  maxWidth: "300px",
                                  height: "200px",
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  backgroundColor: peerCampusTheme.background,
                                  padding: "8px"
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button 
                                variant="outlined"
                                size="small"
                                onClick={() => fileInputRef.current?.click()}
                                sx={{ borderRadius: 1 }}
                              >
                                Change
                              </Button>
                              <Button 
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => setNewItem({ ...newItem, image: null, imageFile: null })}
                                sx={{ borderRadius: 1 }}
                              >
                                Remove
                              </Button>
                            </Box>
                          </Box>
                        )}
                        
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          sx={{ 
                            mt: 4,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: '600',
                            background: peerCampusGradient,
                            '&:hover': { background: peerCampusGradient }
                          }}
                          onClick={() => handleAddItem(mode)}
                          disabled={loading}
                        >
                          {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InlineLoader size={24} />
                              Processing...
                            </Box>
                          ) : (
                            `Submit ${mode === "lost" ? "Lost" : "Found"} Item`
                          )}
                        </Button>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Items List Section */}
                  <Box sx={{ mt: 4 }} ref={browseSectionRef}>
                    <Card sx={{ p: 3, borderRadius: 2 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 3, 
                        flexWrap: 'wrap', 
                        gap: 2 
                      }}>
                        <Typography variant="h6" fontWeight="600">
                          {mode === "lost" ? "Lost Items" : "Found Items"} 
                          <Typography component="span" color={peerCampusTheme.textSecondary} sx={{ ml: 1 }}>
                            ({filteredItems.length} items)
                          </Typography>
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <TextField
                            placeholder="Search items..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            sx={{ width: 200 }}
                            InputProps={{
                              startAdornment: <Search sx={{ mr: 1, color: peerCampusTheme.textSecondary }} />,
                              sx: { borderRadius: 1 }
                            }}
                          />
                          
                          <TextField
                            select
                            size="small"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            sx={{ width: 120 }}
                            InputProps={{ sx: { borderRadius: 1 } }}
                          >
                            <MenuItem value="newest">Newest</MenuItem>
                            <MenuItem value="oldest">Oldest</MenuItem>
                            <MenuItem value="likes">Most Liked</MenuItem>
                          </TextField>
                        </Box>
                      </Box>

                      {filteredItems.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                          <Search sx={{ fontSize: 48, color: peerCampusTheme.textSecondary, mb: 2 }} />
                          <Typography variant="h6" color={peerCampusTheme.textSecondary}>
                            No items found
                          </Typography>
                          <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                            Try adjusting your search or filters
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          {filteredItems.map((item, index) => (
                            <Grid 
                              item 
                              xs={12} 
                              sm={6} 
                              md={4} 
                              lg={3}
                              key={item.id}
                            >
                              <ItemCard item={item} type={mode} />
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Card>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Container>
        </Box>
      </Box>

      {/* Image Scanning Dialog */}
      <Dialog
        open={scanDialog}
        onClose={resetScanDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${peerCampusTheme.border}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="600">
              Scan Found Item
            </Typography>
            <IconButton onClick={resetScanDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {scanResult?.success ? (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom color={peerCampusTheme.success}>
                  Potential Matches Found!
                </Typography>
                <Typography variant="body2" color={peerCampusTheme.textSecondary}>
                  We found {scanResult.matches.length} potential match(es)
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {scanResult.matches.map((item, index) => (
                  <Grid item xs={12} sm={6} key={item.id || index}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardMedia
                        component="img"
                        height="120"
                        image={item.image || "/fallback-image.jpg"}
                        alt={item.title}
                        sx={{ objectFit: 'contain', backgroundColor: peerCampusTheme.background }}
                      />
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle2" fontWeight="600" gutterBottom noWrap>
                          {item.title}
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          size="small"
                          startIcon={<Email />}
                          disabled={isSendingEmail}
                          onClick={() => {
                            handleContactOwner(item);
                            resetScanDialog();
                          }}
                          sx={{ 
                            borderRadius: 1, 
                            mt: 1,
                            background: peerCampusGradient,
                            '&:hover': {
                              background: peerCampusGradient,
                            }
                          }}
                        >
                          Contact Owner
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : !scanningImage ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CameraAlt sx={{ fontSize: 64, color: peerCampusTheme.primary, mb: 3 }} />
              <Typography variant="h6" gutterBottom>
                Upload or Capture Image
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="scan-upload-button"
                  type="file"
                  onChange={handleScanImageUpload}
                  ref={scanFileInputRef}
                />

                <label htmlFor="scan-upload-button">
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{ borderRadius: 2 }}
                  >
                    Upload Image
                  </Button>
                </label>

                <Button
                  variant="contained"
                  onClick={handleOpenCamera}
                  sx={{
                    borderRadius: 2,
                    background: peerCampusGradient,
                    '&:hover': { background: peerCampusGradient },
                  }}
                >
                  Use Camera
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={scanningImage}
                alt="Scan preview"
                style={{
                  width: "100%",
                  maxWidth: "300px",
                  height: "200px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  backgroundColor: peerCampusTheme.background
                }}
              />

              {loading && (
                <Box sx={{ py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Scanning Image...
                  </Typography>
                </Box>
              )}

              {scanResult && !scanResult.success && (
                <Box sx={{ py: 3 }}>
                  <Typography variant="h6" color={peerCampusTheme.warning} gutterBottom>
                    No Match Found
                  </Typography>
                  <Typography variant="body2" color={peerCampusTheme.textSecondary} sx={{ mb: 3 }}>
                    {scanResult.message}
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleEnhanceVersion}
                    sx={{
                      borderRadius: 2,
                      background: peerCampusGradient,
                      '&:hover': { background: peerCampusGradient },
                    }}
                  >
                    Try Enhanced Version
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${peerCampusTheme.border}` }}>
          <Button
            variant="outlined"
            onClick={resetScanDialog}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              borderColor: peerCampusTheme.primary,
              color: peerCampusTheme.primary,
              '&:hover': {
                borderColor: peerCampusTheme.primary,
                backgroundColor: `${peerCampusTheme.primary}0A`,
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog
        open={cameraDialog}
        onClose={handleCloseCamera}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${peerCampusTheme.border}` }}>
          <Typography variant="h6" fontWeight="600">
            Take Photo
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          {!cameraImage ? (
            <Box>
              <Box
                sx={{
                  width: '100%',
                  height: '300px',
                  backgroundColor: '#000',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 3
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
              
              <Button
                variant="contained"
                onClick={handleCaptureImage}
                sx={{
                  borderRadius: 2,
                  background: peerCampusGradient,
                  '&:hover': { background: peerCampusGradient },
                }}
              >
                Capture Photo
              </Button>
            </Box>
          ) : (
            <Box>
              <img
                src={cameraImage}
                alt="Captured preview"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: '300px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  backgroundColor: peerCampusTheme.background
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleRetakePhoto}
                  sx={{ borderRadius: 2, textTransform: 'none', ...gradientOutlinedButtonSx }}
                >
                  <Box component="span" sx={gradientTextSx}>Retake</Box>
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUseCapturedImage}
                  sx={{
                    borderRadius: 2,
                    background: peerCampusGradient,
                    '&:hover': { background: peerCampusGradient },
                  }}
                >
                  Use This Photo
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Success Popup Dialog */}
      <Dialog
        open={showEmailSuccess}
        onClose={() => setShowEmailSuccess(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `linear-gradient(135deg, ${peerCampusTheme.primary}15, ${peerCampusTheme.surface})`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${peerCampusTheme.border}`,
          }
        }}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          {/* Animated Checkmark */}
          <Box sx={{ position: 'relative', width: 100, height: 100, mx: 'auto', mb: 3 }}>
            {/* Outer circle */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${peerCampusTheme.success}20, ${peerCampusTheme.success}10)`,
                border: `2px solid ${peerCampusTheme.success}30`
              }}
            />
            
            {/* Animated checkmark */}
              <motion.svg
                width="100"
                height="100"
                viewBox="0 0 100 100"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fill: 'none',
                  stroke: peerCampusTheme.success,
                  strokeWidth: 6,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round'
                }}
              >

              <motion.path
                d="M25,50 L40,65 L75,30"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              />
            </motion.svg>
            
            {/* Pulsing effect */}
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.5, 0, 0.5]
              }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: `2px solid ${peerCampusTheme.success}`,
              }}
            />
          </Box>

          {/* Success Message */}
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ 
            background: peerCampusGradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 2
          }}>
            Email Sent Successfully!
          </Typography>
          
          <Typography variant="body1" color={peerCampusTheme.textSecondary} sx={{ mb: 1 }}>
            The owner of <strong>{emailSuccessData.itemName}</strong> has been notified.
          </Typography>
          
          <Typography variant="body2" color={peerCampusTheme.textSecondary} sx={{ mb: 3 }}>
            They will contact you soon to arrange the return.
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setShowEmailSuccess(false)}
              sx={{ 
                borderRadius: 2,
                borderColor: peerCampusTheme.border,
                color: peerCampusTheme.text,
                textTransform: 'none',
                ...gradientOutlinedButtonSx,
              }}
            >
              <Box component="span" sx={gradientTextSx}>Continue Browsing</Box>
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowEmailSuccess(false);
                setMode(null);
              }}
              sx={{ 
                borderRadius: 2,
                background: peerCampusGradient,
                '&:hover': {
                  background: peerCampusGradient,
                }
              }}
            >
              Back to Dashboard
            </Button>
          </Box>

          {/* Confetti Effect (Optional decorative elements) */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden' }}>
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ y: -100, x: Math.random() * 100 - 50, rotate: 0 }}
                animate={{ 
                  y: 600, 
                  x: Math.random() * 200 - 100,
                  rotate: 360
                }}
                transition={{ 
                  duration: 1 + Math.random() * 0.5,
                  delay: i * 0.1,
                  ease: "easeInOut"
                }}
                style={{
                  position: 'absolute',
                  fontSize: 24,
                  color: [peerCampusTheme.success, peerCampusTheme.primary, peerCampusTheme.secondary][i % 3],
                  opacity: 0.7,
                }}
              >
                ✨
              </motion.div>
            ))}
          </Box>
        </Box>
      </Dialog>

      {/* Enhanced Notifications */}
      <Snackbar
        open={!!notification.message}
        autoHideDuration={6000}
        onClose={() => setNotification({ message: "", type: "" })}
        anchorOrigin={isSmallMobile ? { vertical: "top", horizontal: "center", } : { vertical: "top", horizontal: "right" }}
        sx={{ top: { xs: 72, sm: 96, md: 112 } }}
      >
        <div>
          <EnhancedAlert 
            severity={notification.type} 
            message={notification.message}
            onClose={() => setNotification({ message: "", type: "" })}
          />
        </div>
      </Snackbar>

      {/* Email Confirmation Snackbar (Secondary Notification) */}
      <Snackbar
        open={showEmailConfirmation}
        autoHideDuration={3000}
        onClose={() => setShowEmailConfirmation(false)}
        anchorOrigin={isSmallMobile ? { vertical: "top", horizontal: "center" } : { vertical: "top", horizontal: "right" }}
        sx={{ top: { xs: 72, sm: 96, md: 112 } }}
      >
        <div>
          <EnhancedAlert
            severity="success"
            message={`Email sent to ${emailConfirmationData.ownerEmail || "owner"}`}
            onClose={() => setShowEmailConfirmation(false)}
          />
        </div>
      </Snackbar>

      {/* Item Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, item: null })}
        maxWidth="md"
        fullWidth
      >
        {detailDialog.item && (
          <>
            <DialogTitle sx={{ borderBottom: `1px solid ${peerCampusTheme.border}` }}>
              <Typography variant="h6" fontWeight="600">
                {detailDialog.item.title}
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <img
                    src={detailDialog.item.image}
                    alt={detailDialog.item.title}
                    style={{
                      width: '100%',
                      borderRadius: 8,
                      objectFit: 'contain',
                      maxHeight: '300px',
                      backgroundColor: peerCampusTheme.background
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" color={peerCampusTheme.textSecondary} sx={{ mb: 3 }}>
                    {detailDialog.item.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ color: peerCampusTheme.textSecondary }} />
                      <Typography>{detailDialog.item.location}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email sx={{ color: peerCampusTheme.textSecondary }} />
                      <Typography>{detailDialog.item.email}</Typography>
                    </Box>
                    {detailDialog.item.reward && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CardGiftcard sx={{ color: peerCampusTheme.textSecondary }} />
                        <Typography>Reward: {detailDialog.item.reward}</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ p: 2, borderTop: `1px solid ${peerCampusTheme.text}` }}>
              <Button
                onClick={() => setDetailDialog({ open: false, item: null })}
                sx={{
                  color: peerCampusTheme.primary,
                  '&:hover': {
                    backgroundColor: `${peerCampusTheme.primary}0A`,
                  },
                }}
              >
                Close
              </Button>
              <Button 
                variant="contained"
                onClick={() => handleContactOwner(detailDialog.item)}
                disabled={isSendingEmail}
                sx={{
                  background: peerCampusGradient,
                  '&:hover': {
                    background: peerCampusGradient,
                  }
                }}
              >
                Contact Owner
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {!embedded && <PostLoginFooter />}
    </Box>
  );
}